'use client';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CheckIcon } from 'lucide-react';

import { cn } from '~/lib/utils';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuContent: React.FC<
  React.ComponentProps<typeof DropdownMenuPrimitive.Content>
> = ({ className, sideOffset = 4, ...props }) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-60 overflow-hidden rounded-xl bg-surface-neutral p-2 shadow-[0_2px_16px_0_rgba(0,0,0,0.12)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
);

DropdownMenuContent.displayName = 'DropdownMenuContent';

const DropdownMenuItem: React.FC<
  React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
> = ({ className, inset, ...props }) => (
  <DropdownMenuPrimitive.Item
    className={cn(
      'relative flex cursor-pointer items-center rounded-lg px-3 py-3 text-[15px] text-[#222] outline-hidden transition-colors duration-100 ease-standard-curve select-none hover:bg-surface-neutral-hovered focus:bg-surface-neutral-hovered data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-50',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

const DropdownMenuRadioItem: React.FC<
  React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem> & {
    hideIcon?: boolean;
  }
> = ({ className, children, hideIcon, ...props }) => (
  <DropdownMenuPrimitive.RadioItem
    className={cn(
      'relative flex cursor-pointer items-center rounded-xl py-4 pr-12 pl-4 text-md font-medium text-neutral ring-0 select-none hover:bg-[#F0F1F2] focus:bg-[#F0F1F2] focus:outline-none data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    {...props}
  >
    {children}
    {!hideIcon && (
      <span className="absolute right-2.5 flex size-6 items-center justify-center rounded-full border border-[#C4C8CB]">
        <DropdownMenuPrimitive.ItemIndicator className="absolute inset-0 flex items-center justify-center rounded-full bg-[#000] text-inverted">
          <CheckIcon className="size-4 stroke-3" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
    )}
  </DropdownMenuPrimitive.RadioItem>
);

DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

const DropdownMenuLabel: React.FC<
  React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
> = ({ className, inset, ...props }) => (
  <DropdownMenuPrimitive.Label
    className={cn(
      'px-1.5 py-1.5 text-sm font-semibold',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
);

DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const DropdownMenuSeparator: React.FC<
  React.ComponentProps<typeof DropdownMenuPrimitive.Separator>
> = ({ className, ...props }) => (
  <DropdownMenuPrimitive.Separator
    className={cn('my-2 h-px bg-borders-neutral-subdued', className)}
    {...props}
  />
);

DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
